import React from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import styled from 'styled-components/native';
import TextInputWithButtons from '../TextInputWithButtons';
import { TextList } from './TextList';
import {
  GetItemText,
  RenderListItem,
  TransformTextToItem,
} from './TextListInputTypes';

export interface TextListInputProps {
  /**
   * value
   * An arbitrary array of objects to be rendered in the list
   */
  value?: any[];

  /**
   * onChange
   * Callback called when to value property changes either by the addition of a new list item or the removal of one
   */
  onChange(value: any[]): void;

  /**
   * renderListItm
   * optional callback, useful when you want custom representation of your data in the list e.g. if you are rendering
   * a list of people with avatars, this transform allows you to add the avatars and display the information in any
   * way you want
   */
  renderListItem?: RenderListItem;

  /**
   * transformTextToItem
   * optional callback, useful when value is a list of objects. with this callback you can transform objects to the text
   * representation which ever way you want. If this callback is not passed, the component will assume that the list is a simple
   * list of strings
   */
  transformTextToItem?: TransformTextToItem;
  /**
   * renderListItm
   * optional callback, useful when value is a list of objects. with this callback you can transform objects to the text
   * representation which ever way you want. If this callback is not passed, the component will assume that the list is a simple
   * list of strings
   */
  getItemText?: GetItemText;
  placeholder?: string;
  style?: StyleProp<ViewStyle>;
  itemStyle?: ViewStyle;
}

const empty_value: any[] = [];

/**
 * TextListInput takes text as input and can produce an arbitrary, ordered list of objects of any kind
 * It can also render arbitrary lists of objects
 */
export function TextListInput(props: TextListInputProps): JSX.Element {
  const {
    onChange,
    style,
    value = empty_value,
    placeholder = 'What to add to list',
    renderListItem = renderListItemDefault,
    transformTextToItem = transformTextToItemDefault,
    getItemText = getItemTextDefault,
  } = props;

  const onInputSubmit = (text: string) =>
    onChange([...value, transformTextToItem(text)]);

  return (
    <Container style={style}>
      <TextInputWithButtons
        placeholder={placeholder}
        onSubmit={onInputSubmit}
      />

      <TextList
        itemStyle={props.itemStyle}
        items={value}
        onPressItem={(_, index) => {
          if (onChange) {
            const newValue = [...value];
            newValue.splice(index, 1);
            onChange(newValue);
          }
        }}
        getItemText={getItemText}
        renderListItem={renderListItem}
      />
    </Container>
  );
}

// Default transforms, assume the data being passed is a list of strings
const renderListItemDefault = (
  item: any,
  getItemText: (item: any) => string
): React.ReactNode => <ListItemText value={getItemText(item)} />;

const getItemTextDefault = (item: any): string => item as string;

const transformTextToItemDefault = (text: string): any => text;

const Container = styled.View`
  align-self: stretch;
`;

const ListItemText = styled.TextInput`
  flex: 1;
`;
